import { Link } from "react-router-dom";
import { ArrowRight, Layers, PenTool, Box, Palette, Boxes, Ruler, Workflow, Info } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { SectionHeading, QuotationCTA } from "@/components/site/Shared";
import { useSEO } from "@/hooks/useSEO";
import { IMAGES } from "@/data/catalog";

const SERVICES = [
  { icon: Palette, title: "Residential Interior Design", desc: "Homes designed around how you live — HDB, condo and landed." },
  { icon: Layers, title: "Commercial Interior Design", desc: "Workspaces and retail that reflect your brand and operations." },
  { icon: Ruler, title: "Space Planning", desc: "Efficient, functional layouts that make the most of every square foot." },
  { icon: PenTool, title: "Concept Design", desc: "Mood, materials and direction established before build." },
  { icon: Box, title: "3D Visualisation", desc: "Photorealistic renders so you can see it before we build it." },
  { icon: Boxes, title: "Material & Finish Selection", desc: "Curated, durable material palettes suited to your budget." },
  { icon: Workflow, title: "Design & Build Coordination", desc: "Seamless hand-off from design to our renovation and fabrication teams." },
];

export default function InteriorDesign() {
  useSEO({
    title: "Interior Design & Renovation Singapore",
    description: "Interior design and space planning by WAAN METAL — concept design, 3D visualisation and design-and-build coordination for homes and businesses in Singapore.",
  });
  return (
    <Layout>
      <section className="relative bg-[#0A1128] py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0"><img src={IMAGES.interior1} alt="" className="w-full h-full object-cover opacity-25" /><div className="absolute inset-0 bg-gradient-to-br from-[#0A1128] via-[#0A1128]/88 to-[#1E293B]/70" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="eyebrow mb-3">Interior Design Studio</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight max-w-3xl">Design that is built to be built.</h1>
          <p className="mt-5 text-slate-300 max-w-2xl text-lg">Concept to completion — our design studio works hand-in-hand with our renovation and fabrication teams.</p>
          <Link to="/request-quotation" className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#B87333] to-[#D97706] text-[#0A1128] font-bold hover:-translate-y-0.5 transition-all">
            Start a Design Enquiry <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="What We Offer" title="Interior design services" center />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s) => (
              <div key={s.title} className="p-7 rounded-2xl border border-slate-100 bg-slate-50 hover:border-[#B87333]/40 hover:shadow-lg transition-all" data-testid={`interior-service-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#B87333] to-[#D97706] flex items-center justify-center text-[#0A1128] mb-4"><s.icon size={22} /></div>
                <h3 className="text-lg font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Concepts & Completed Work" title="Concepts vs. real projects" subtitle="We clearly label conceptual renders and completed projects so you always know what you're looking at." />
          <div className="grid md:grid-cols-2 gap-6">
            {[IMAGES.interior1, IMAGES.interior2].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden h-72">
                <img src={img} alt="Interior concept" className="w-full h-full object-cover" />
                <span className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-[#B87333] text-[#0A1128] text-xs font-bold font-mono-label uppercase tracking-wider">Concept</span>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-xl bg-white border border-slate-200 p-5 flex gap-3 text-sm text-slate-600">
            <Info size={18} className="shrink-0 mt-0.5 text-[#B87333]" />
            <p>Images above are conceptual visualisations. Verified completed projects are published on our Projects page and are clearly labelled as real projects.</p>
          </div>
        </div>
      </section>

      <QuotationCTA />
    </Layout>
  );
}
